import AddIcon from "@mui/icons-material/Add";
import { Box, Container, Fab, Grid, Tooltip } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import AutocompleteWithQuery from "components/AutocompleteWithQuery";
import DownloadIconButton from "components/IconButtons/DownloadIconButton";
import UploadIconButton from "components/IconButtons/UploadIconButton";
import LazyTable, {
  DEFAULT_ROWS_PER_PAGE,
  TableCellType,
  TableSortType,
} from "components/LazyTable";
import SnackbarMessage from "components/SnackbarMessage";
import useGlobalContext from "hooks/globalData";
import { useStrictQuery } from "hooks/useStrictQuery";
import useTitle from "hooks/useTitle";
import { useState } from "react";
import { useIntl } from "react-intl";
import { useQueryClient } from "react-query";
import { Link } from "react-router-dom";
import {
  AuthorityFilter,
  ListRequestOfAuthorityFilter,
  ListRequestOfProcessingFilter,
  ProcessingModel,
  ResponseCode,
} from "services/KnowfullyAdminClient";
import contextStore from "stores/contextStore";
import { Severity, SnackbarMessageProps } from "types/common-types";
import { convertToUTC, standardDateFormat } from "utils/dateUtils";
import translateError from "utils/translateError";
import { UploadSubmissionStatusDialog } from "./UploadSubmissionStatusDialog";

const PreviouslyExportedReports = () => {
  const {
    submissionService,
    authorityService,
    addCreditSubmissionReportsUrl,
  } = useGlobalContext();
  const queryClient = useQueryClient();
  const { organizationId } = contextStore.useState();
  const { formatMessage: t } = useIntl();
  const [authorityId, setAuthorityId] = useState<string | undefined>();
  const [startDate, setStartDate] = useState<Date | undefined>();
  const [endDate, setEndDate] = useState<Date | undefined>();
  const [order, setOrder] = useState<TableSortType>("desc");
  const [orderBy, setOrderBy] = useState("processDate");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);
  const [
    procesingToUploadSubmissionStatus,
    setProcesingToUploadSubmissionStatus,
  ] = useState<ProcessingModel | undefined>();

  useTitle(t({ id: "creditSubmissionReportsListTitle" }));

  const fetchedAuthorities = useStrictQuery(
    ["getAuthorities", organizationId],
    () =>
      authorityService.getAuthorities(
        ListRequestOfAuthorityFilter.fromJS({
          parameters: AuthorityFilter.fromJS({
            manualSubmissionSettingValue: true,
          }),
        })
      )
  );

  const { data, isFetching } = useStrictQuery(
    [
      "getPreviouslyExportedProcessings",
      organizationId,
      authorityId,
      endDate,
      startDate,
      page,
      rowsPerPage,
      orderBy,
      order,
    ],
    () =>
      submissionService.getPreviouslyExportedProcessings(
        ListRequestOfProcessingFilter.fromJS({
          pageNumber: page,
          pageSize: rowsPerPage,
          sortField: orderBy,
          sortDescending: order === "desc",
          parameters: {
            authorityId,
            endDate: endDate ? convertToUTC(endDate) : undefined,
            startDate: startDate ? convertToUTC(startDate) : undefined,
          },
        })
      ),
    { keepPreviousData: true }
  );

  async function downloadReport(processingId: string): Promise<void> {
    try {
      const reportFileUrl = await submissionService.getProcessingReportFileUrl(
        processingId
      );

      if (reportFileUrl.responseCode === ResponseCode.NotFound) {
        throw new Error(t({ id: "errorReportNotFound" }));
      }

      if (reportFileUrl.responseCode !== ResponseCode.OK) {
        throw new Error(t({ id: "errorDownloadingReport" }));
      }

      window.open(reportFileUrl.data);
    } catch (error) {
      setSnackbarMessage(
        new SnackbarMessageProps(translateError(error, t), Severity.Error, 5000)
      );
    }
  }

  const rows = data?.data ?? [];

  const cells: Array<TableCellType> = [
    {
      accessor: "processDate",
      align: "left",
      width: 0,
      type: "date",
      label: t({ id: "exportDate" }),
    },
    {
      accessor: "certificateStartDate",
      align: "left",
      width: 0,
      type: "date",
      noSort: true,
      label: t({ id: "certStartDate" }),
    },
    {
      accessor: "certificateEndDate",
      align: "left",
      width: 0,
      type: "date",
      noSort: true,
      label: t({ id: "certEndDate" }),
    },
    {
      accessor: "authorityName",
      customSortAccessor: "authority.name",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "designationAuthority" }),
    },
    {
      accessor: "organizationName",
      customSortAccessor: "organization.name",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "organization" }),
    },
    {
      accessor: "statusName",
      customSortAccessor: "status.name",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "statusLabel" }),
    },
    {
      accessor: "value",
      align: "center",
      width: 0,
      type: "node",
      View: ({ value }: { value: ProcessingModel }) => (
        <DownloadIconButton onClick={() => downloadReport(value.id)} />
      ),
      noSort: true,
      label: t({ id: "download" }),
    },
    {
      accessor: "value",
      align: "center",
      width: 200,
      type: "node",
      View: ({ value }: { value: ProcessingModel }) => (
        <UploadIconButton
          disabled={!value.isUploadSubmissionStatusAvailable}
          label={
            value.isUploadSubmissionStatusAvailable
              ? "uploadSubmissionStatus"
              : "resultsAreAlreadyUploaded"
          }
          onClick={() => setProcesingToUploadSubmissionStatus(value)}
        />
      ),
      noSort: true,
      label: t({ id: "uploadSubmissionStatus" }),
    },
  ];

  return (
    <Container>
      <Box mb={3} textAlign="center">
        <Link
          to={addCreditSubmissionReportsUrl()}
          style={{ textDecoration: "none" }}
        >
          <Tooltip title={t({ id: "addCreditSubmissionReports" })}>
            <Fab aria-label="add" color="primary">
              <AddIcon />
            </Fab>
          </Tooltip>
        </Link>
      </Box>

      <Box mb={3}>
        <Grid container spacing={2}>
          <Grid item sm={12} md={6}>
            <AutocompleteWithQuery
              value={authorityId}
              onChange={(id) => setAuthorityId(id)}
              disabled={
                isFetching ||
                (fetchedAuthorities.isFetched &&
                  fetchedAuthorities.data?.data?.length === 0)
              }
              labelField="authorityTypeName"
              fetchedData={fetchedAuthorities}
              failMessage={t({ id: "selectFetchFailed" })}
              textFieldProps={{
                variant: "outlined",
              }}
              label={t({ id: "authority" })}
            />
          </Grid>

          <Grid item sm={6} md={3}>
            <DatePicker
              label={t({ id: "submissionStartDate" })}
              disabled={isFetching}
              value={startDate}
              format={standardDateFormat}
              onChange={(date: any) => setStartDate(date)}
              slotProps={{
                textField: { fullWidth: true, variant: "standard" },
                actionBar: {
                  actions: ["clear", "cancel"],
                },
              }}
            />
          </Grid>
          <Grid item sm={6} md={3}>
            <DatePicker
              label={t({ id: "submissionEndDate" })}
              disabled={isFetching}
              value={endDate}
              format={standardDateFormat}
              onChange={(date: any) => setEndDate(date)}
              slotProps={{
                textField: { fullWidth: true, variant: "standard" },
                actionBar: {
                  actions: ["clear", "cancel"],
                },
              }}
            />
          </Grid>
        </Grid>
      </Box>

      <LazyTable
        {...{
          rowCount: data?.totalRecords ?? 0,
          rows: rows,
          loading: isFetching,
          title: t({ id: "previouslyExportedFiles" }),
          dense: true,
          cells,
          page,
          setPage,
          order,
          setOrder,
          orderBy,
          setOrderBy,
          rowsPerPage,
          setRowsPerPage,
        }}
      />

      <UploadSubmissionStatusDialog
        open={!!procesingToUploadSubmissionStatus}
        onClose={() => setProcesingToUploadSubmissionStatus(undefined)}
        processing={procesingToUploadSubmissionStatus}
        onUpload={() => {
          queryClient.invalidateQueries("getPreviouslyExportedProcessings");
          setSnackbarMessage(
            new SnackbarMessageProps(
              t({ id: "uploadingSubmissionStatusFileCompleted" })
            )
          );
        }}
      />

      <SnackbarMessage
        snackbarMessage={snackbarMessage}
        setSnackbarMessage={setSnackbarMessage}
      />
    </Container>
  );
};

export default PreviouslyExportedReports;
